import {mount} from 'svelte';

import * as Utils from '@js/modules/utils';
import {videoLazyLoader} from '@js/modules/videoLazyLoader';
import AnimateElement from '@js/modules/animateElement';
import {mountAfterMapUsable, importEarly} from '@js/managers/mountAfterMapUsable';

import HomeSearch from '@js/components/home/HomeSearch.svelte';
// import HomeComputedDistance from '@js/components/home/HomeComputedDistance.svelte';
import RidesRecents from '@js/components/ride/list/RidesRecents.svelte';
import HomeOnboarding from '@js/components/home/HomeOnboarding.svelte';

const HomeMap = () => import(/* webpackChunkName: "home-map" */ '@js/components/home/HomeMap.svelte');
const HomeAround = () => import('@js/components/home/HomeAround.svelte');
const HomeRouteDetails = () => import('@js/components/home/HomeRouteDetails.svelte');
const HomeRouteInfo = () => import('@js/components/home/HomeRouteInfo.svelte');
const HomeTrackImport = () => import('@js/components/home/HomeTrackImport.svelte');
const RideCreation = () => import('@js/components/ride/RideCreation.svelte');
const ShareUrlButton = () => import('@js/components/ride/share/ShareUrlButton.svelte');

// Fetched as soon as this script runs, only its mount waits for the page; a failed fetch is tried again.
const homeMapModule = importEarly(HomeMap);
// Idle may not come for seconds on a busy mobile boot: the map is mounted by then at the latest.
const MAP_MOUNT_MAX_IDLE_WAIT = 1000;

class HomeManager {
    _components = {
        $searchForm: document.getElementById('home-search'),
        $map: document.getElementById('home-map-container'),
        // $computedDistance: document.getElementById('home-computed-distance'),
        $routeDetails: document.getElementById('home-route-details'),
        $routeInfo: document.getElementById('home-route-info'),
        $trackImport: document.getElementById('home-track-import'),
        $rideCreation: document.getElementById('home-ride-creation'),
        $rideShare: document.getElementById('home-ride-share'),
        $ridesRecents: document.getElementById('home-rides-recents'),
        $homeAround: document.getElementById('home-around'),
        $onboarding: document.getElementById('home-onboarding')
    };

    /* Public methods
    ******************** */
    // constructor() {
    // }

    initialize = () => {
        this._mountComponents();

        this._mountAsyncComponents();

    AnimateElement.initialize();

        videoLazyLoader();
    };

    /* Private methods
    ******************** */
    _mountComponents = () => {
        mount(HomeSearch, {
            target: this._components.$searchForm,
            props: {}
        });

        mount(RidesRecents, {
            target: this._components.$ridesRecents,
            props: {
                class: 'main-section mt-24',
                titleClass: 'home-title mb-6'
            }
        });

        if (this._components.$onboarding) {
            mount(HomeOnboarding, {
                target: this._components.$onboarding,
                props: {}
            });
        }

        // if (this._components.$computedDistance) {
        //     mount(HomeComputedDistance, {
        //         target: this._components.$computedDistance,
        //         props: {}
        //     });
        // }
    };

    // « Create a planned ride here » from the plan page: the place, given by the server to its owner only
    _planPoi = () => {
        const data = this._components.$rideCreation?.dataset;
        if (!data?.planPoiId) {
            return null;
        }

        return {
            id: Number(data.planPoiId),
            title: data.planPoiTitle,
            lat: parseFloat(data.planPoiLat),
            lng: parseFloat(data.planPoiLng),
            rideType: data.planPoiRideType,
            planDateType: data.planPoiDateType || undefined,
            plannedAt: data.planPoiPlannedAt || undefined
        };
    };

    _mountAsyncComponents = () => {
        Utils.onPageReady(() => {
            homeMapModule()
                .then(({default: HomeMapComponent}) => {
                    mount(HomeMapComponent, {
                        target: this._components.$map,
                        props: {planPoi: this._planPoi()}
                    });

                    // Class used for preserving space on initial load
                    this._components.$map.classList.remove('home-map-container');
                });

            HomeTrackImport()
                .then(({default: HomeTrackImportComponent}) => {
                    mount(HomeTrackImportComponent, {
                        target: this._components.$trackImport,
                        props: {}
                    });
                });

            HomeAround()
                .then(({default: HomeAroundComponent}) => {
                    mount(HomeAroundComponent, {
                        target: this._components.$homeAround,
                        props: {}
                    });
                });

            // They only show once a route is drawn: mounted after the map's boot, never during it.
            mountAfterMapUsable([
                () => HomeRouteDetails()
                    .then(({default: HomeRouteDetailsComponent}) => {
                        mount(HomeRouteDetailsComponent, {
                            target: this._components.$routeDetails,
                            props: {}
                        });
                    }),
                () => HomeRouteInfo()
                    .then(({default: HomeRouteInfoComponent}) => {
                        mount(HomeRouteInfoComponent, {
                            target: this._components.$routeInfo,
                            props: {}
                        });
                    }),
                () => RideCreation()
                    .then(({default: RideCreationComponent}) => {
                        mount(RideCreationComponent, {
                            target: this._components.$rideCreation,
                            props: {
                                home: true,
                                planPoiId: this._planPoi()?.id ?? null,
                                planPoiDateType: this._planPoi()?.planDateType,
                                planPoiPlannedAt: this._planPoi()?.plannedAt,
                                defaultPlanned: this._planPoi() ? true : undefined
                            }
                        });
                    }),
                () => ShareUrlButton()
                    .then(({default: ShareUrlButtonComponent}) => {
                        mount(ShareUrlButtonComponent, {
                            target: this._components.$rideShare,
                            props: {}
                        });
                    })
            ]);
        }, undefined, {maxIdleWait: MAP_MOUNT_MAX_IDLE_WAIT});
    };

    _focusOnMap = (event) => {
        event.preventDefault();

        setTimeout(() => Utils.scrollTo('home-map'), 150);
    };
}

export default new HomeManager();
